import { useState } from 'react';
import { DatePicker, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { OwnerType } from '@never2busy/shared';
import { auditApi, type AuditAction, type AuditLog } from '../../../api/endpoints/audit';
import { PageHeader } from '../../../components/common/PageHeader';
import { UserSelect } from '../../../components/common/UserSelect';

const { RangePicker } = DatePicker;

const ACTION_LABEL: Record<AuditAction, string> = {
  LOGIN_OK: 'Login OK',
  LOGIN_FAIL: 'Login fallido',
  LOGOUT: 'Logout',
  PASSWORD_CHANGE: 'Cambio de contraseña',
  CREATE: 'Crear',
  UPDATE: 'Actualizar',
  DELETE: 'Eliminar',
  EXPORT: 'Exportar',
};

const ACTION_COLOR: Partial<Record<AuditAction, string>> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  LOGIN_FAIL: 'orange',
  EXPORT: 'purple',
};

const OWNER_LABEL: Record<string, string> = {
  ACCOUNT: 'Cuenta',
  PERSON: 'Contacto',
  PROJECT: 'Proyecto',
  ACTIVITY: 'Actividad',
  SALE: 'Venta',
};

export function AuditLogPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [userId, setUserId] = useState<number | undefined>();
  const [action, setAction] = useState<AuditAction | undefined>();
  const [ownerType, setOwnerType] = useState<OwnerType | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['audit', { page, pageSize, userId, action, ownerType, range }],
    queryFn: () =>
      auditApi.list({
        page,
        pageSize,
        userId,
        action,
        ownerType,
        from: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
        to: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
      }),
  });

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Fecha',
      dataIndex: 'timestamp',
      width: 170,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Usuario',
      width: 200,
      render: (_, row) =>
        row.user ? `${row.user.firstName} ${row.user.lastName}` : '—',
    },
    {
      title: 'Acción',
      dataIndex: 'action',
      width: 140,
      render: (a: AuditAction) => <Tag color={ACTION_COLOR[a]}>{ACTION_LABEL[a] ?? a}</Tag>,
    },
    {
      title: 'Entidad',
      width: 140,
      render: (_, row) =>
        row.ownerType
          ? `${OWNER_LABEL[row.ownerType] ?? row.ownerType}${row.ownerId ? ` #${row.ownerId}` : ''}`
          : '—',
    },
    { title: 'Detalle', dataIndex: 'message', ellipsis: true },
    { title: 'IP', dataIndex: 'ip', width: 130 },
  ];

  const resetPage = (): void => setPage(1);

  return (
    <>
      <PageHeader title="Auditoría" crumbs={[{ title: 'Admin' }, { title: 'Auditoría' }]} />

      <Space wrap style={{ marginBottom: 16 }}>
        <div style={{ width: 220 }}>
          <UserSelect
            value={userId}
            onChange={(v) => {
              setUserId(v);
              resetPage();
            }}
            placeholder="Todos los usuarios"
          />
        </div>
        <Select
          allowClear
          placeholder="Acción"
          style={{ width: 180 }}
          value={action}
          onChange={(v) => {
            setAction(v);
            resetPage();
          }}
          options={(Object.keys(ACTION_LABEL) as AuditAction[]).map((a) => ({
            value: a,
            label: ACTION_LABEL[a],
          }))}
        />
        <Select
          allowClear
          placeholder="Entidad"
          style={{ width: 160 }}
          value={ownerType}
          onChange={(v) => {
            setOwnerType(v);
            resetPage();
          }}
          options={Object.values(OwnerType).map((o) => ({
            value: o,
            label: OWNER_LABEL[o] ?? o,
          }))}
        />
        <RangePicker
          value={range ?? undefined}
          onChange={(v) => {
            setRange(v as [Dayjs | null, Dayjs | null] | null);
            resetPage();
          }}
          format="DD/MM/YYYY"
        />
      </Space>

      <Table<AuditLog>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        size="small"
      />
    </>
  );
}
